'use client'

import React, { useState, useEffect } from 'react'
import { toZonedTime } from 'date-fns-tz'
import { AnalogClock } from '@hoseinh/react-analog-clock'
import { cn } from '@/lib/utils'
import { resolveTimezone } from '@/lib/platformTools/library/currentTimeTool'
import { EMPTY_WIDGET_SURFACE_CLASS } from './widget-surface'

interface ClockWidgetProps {
  city?: string
  className?: string
  onClick?: () => void
}

const CLOCK_FACE_BACKGROUND =
  'data:image/svg+xml,%3Csvg width%3D%2288%22 height%3D%2288%22 viewBox%3D%220 0 88 88%22 xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg stroke%3D%22currentColor%22 stroke-opacity%3D%220.45%22 stroke-width%3D%221.5%22 stroke-linecap%3D%22round%22%3E%3Cpath d%3D%22M44 7v7%22/%3E%3Cpath d%3D%22M44 74v7%22/%3E%3Cpath d%3D%22M7 44h7%22/%3E%3Cpath d%3D%22M74 44h7%22/%3E%3Cpath d%3D%22M25.5 12l3.5 6%22/%3E%3Cpath d%3D%22M59 70l3.5 6%22/%3E%3Cpath d%3D%22M12 25.5l6 3.5%22/%3E%3Cpath d%3D%22M70 59l6 3.5%22/%3E%3Cpath d%3D%22M62.5 12l-3.5 6%22/%3E%3Cpath d%3D%22M29 70l-3.5 6%22/%3E%3Cpath d%3D%22M76 25.5l-6 3.5%22/%3E%3Cpath d%3D%22M18 59l-6 3.5%22/%3E%3C/g%3E%3C/svg%3E'

function formatInTimezone(
  date: Date,
  timeZone: string,
  options: { time?: Intl.DateTimeFormatOptions; date?: Intl.DateTimeFormatOptions }
): { timeStr: string; dateStr: string } {
  try {
    const timeStr = date.toLocaleTimeString(undefined, {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      ...options.time
    })
    const dateStr = date.toLocaleDateString(undefined, {
      timeZone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      ...options.date
    })
    return { timeStr, dateStr }
  } catch {
    return {
      timeStr: date.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      dateStr: date.toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      })
    }
  }
}

/** Returns a Date that displays as the current time in the given timezone (for analog clock hands). */
function getDisplayTimeInZone(utcDate: Date, timeZone: string): Date {
  try {
    return toZonedTime(utcDate, timeZone)
  } catch {
    return utcDate
  }
}

export function ClockWidget({ city, className, onClick }: ClockWidgetProps) {
  const [now, setNow] = useState(() => new Date())
  const timeZone = city?.trim()
    ? resolveTimezone(city)
    : Intl.DateTimeFormat().resolvedOptions().timeZone

  useEffect(() => {
    const tick = () => setNow(new Date())
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [])

  const displayDate = getDisplayTimeInZone(now, timeZone)
  const { timeStr, dateStr } = formatInTimezone(now, timeZone, {})
  const secondHandKey = Math.floor(displayDate.getTime() / 1000)

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(EMPTY_WIDGET_SURFACE_CLASS, 'flex-row items-center gap-3', className)}
      aria-label={city ? `Current time in ${city}` : 'Current time'}
    >
      {/* Analog clock: key per second so second hand updates live */}
      <div className="flex shrink-0 items-center justify-center">
        <AnalogClock
          key={secondHandKey}
          staticDate={displayDate}
          size="76px"
          showMinuteHand
          showSecondHand
          showBorder
          showHandBase
          smooth
          whiteNumbers={false}
          square={false}
          numbersType="lines"
          borderColor="hsl(var(--border))"
          backgroundColor="hsl(var(--card))"
          customBg={CLOCK_FACE_BACKGROUND}
          handColor={{
            hour: 'hsl(var(--foreground))',
            minute: 'hsl(var(--foreground))',
            second: '#e74c3c'
          }}
          handBaseColor="hsl(var(--foreground))"
          handLength={{ hour: '22px', minute: '28px', second: '28px' }}
          handThickness={{ hour: '2px', minute: '1.5px', second: '1px' }}
        />
      </div>
      {/* Digital time and date on the right */}
      <div className="flex min-w-0 flex-1 flex-col items-start justify-center gap-0.5">
        <div className="text-xs text-muted-foreground">{city ? city : 'Local time'}</div>
        <div className="text-xl font-semibold tabular-nums leading-tight text-foreground">
          {timeStr}
        </div>
        {dateStr && <div className="text-xs text-muted-foreground">{dateStr}</div>}
      </div>
    </button>
  )
}
